import * as React from "react";
import { ChangeEvent, useState } from "react";
import { MenuItem } from "@mui/material";
import classNames from "classnames";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { ReactComponent as IconClose } from "../../assets/svg/close-icon.svg";
import { FieldTextArea } from "../../components/fields/TextArea/TextArea";
import { FieldSwitch } from "../../components/fields/FieldSwitch/FieldSwitch";
import { BrandProductList, IProduct } from "./components/ProductList";
import { BrandProList } from "./components/ProList";
import { IContentType } from "./components/ContentTypeList";
import { ISinglePro } from "../../components/singlePro/state";
import { FieldText } from "../../components/fields/Text/Text";
import { uploadImage } from "../profile/helper";
import { useUser } from "../../hooks/useUser";
import { isInternal } from "../../utils";
import { handleConflictError } from "../../utils/errorHandler";
import { ProductImage } from "../admin/productsV2/productModal/ProductImage";
import ContentTypeSelector from "./ContentTypeSelector";
import { SlackChannelPicker } from "../../components/SlackChannelPicker/SlackChannelPicker";
import { CustomFieldSelect } from "../../components/fields/CustomFieldSelect";
import { BriefBrandAssetsFields } from "../../components/Brief/BriefBrandAssetsFields";
import { BRIEF_BRAND_ASSET_KEYS } from "../../components/Brief/constants";
import { brandBriefAssetUpload } from "../../api/endpoints";
import {
  BRAND_PLANS,
  BrandPlan,
  INTERVAL_LABELS,
  PDP_SCORE_INTERVALS,
  PLAN_LABELS,
  brandIsNetwork,
  formatProductCap,
  isNetworkPlan,
} from "../../utils/plans";

export type InputOnChange = ChangeEvent<HTMLInputElement>;
export type SaveChangesParams = (
  payload: any,
  productIds: Array<string>,
  newProducts: Array<IProduct>,
  proIds: Array<number>,
  contentTypeIds: Array<number>,
) => Promise<void>;

export const SignupSchema = Yup.object().shape({
  description: Yup.string().required("Description is required"),
  name: Yup.string().required("Name is required"),
});

export interface ImageProps {
  source: string;
  error?: string;
  file?: any;
  extension?: string;
}

export type BrandFormHiddenField =
  | "embed"
  | "plan"
  | "preApproval"
  | "notifications"
  | "contentTypes";

interface Props {
  brandData?: any;
  prevProducts?: Array<IProduct>;
  prevPros?: Array<ISinglePro>;
  saveChanges: SaveChangesParams;
  isDialog?: boolean;
  onNameChange?: (name: string) => void;
  /** Pre-fills the Name field (e.g. text typed in an upstream autocomplete). */
  initialBrandName?: string;
  /** Initial plan when not editing an existing brand (internal-only field). */
  defaultPlan?: BrandPlan;
  /** Products with PDP scoring active, for the "N / max" meter next to the plan fields. */
  productCount?: number;
  /** Fields to suppress — used by the inline-create modal to show a minimal form. */
  hiddenFields?: BrandFormHiddenField[];
}

// The cap shown in the scoring meter: the draft value, or the saved one
// while the field is blank.
const capLabel = (values: any, saved: number | null | undefined): string => {
  if (values.unlimited_products) return "Unlimited";
  if (values.max_products === "") return formatProductCap(saved);
  return String(values.max_products);
};

const BrandForm: React.FC<Props> = (props) => {
  const {
    brandData,
    prevProducts,
    prevPros,
    saveChanges,
    isDialog,
    onNameChange,
    initialBrandName,
    defaultPlan,
    productCount,
    hiddenFields,
  } = props;
  const isHidden = (field: BrandFormHiddenField) =>
    hiddenFields?.includes(field) ?? false;
  const [logo, setLogo] = useState<ImageProps>();
  const [products, setProducts] = useState<Array<IProduct>>([]);
  const [newProducts, setNewProducts] = useState<Array<IProduct>>([]);
  const [pros, setPros] = useState<Array<ISinglePro>>([]);
  const [isSubmitting, setISubmitting] = useState(false);
  const [contentTypes, setContentTypes] = useState<Array<IContentType>>([]);
  const { role } = useUser();
  // Sales may create a brand but sees existing ones read-only (TDC-763).
  const isSales = role === "SALES_TEAM" && !!brandData;

  const handleAddPros = (pro: ISinglePro) => {
    const tmpPros = [...pros];
    if (pro.userid && !tmpPros.find((e) => e.userid === pro.userid)) {
      tmpPros.push(pro);
      setPros(tmpPros);
    }
  };
  const handleRemovePros = (i: number) => {
    const tmpPros = [...pros];
    tmpPros.splice(i, 1);
    setPros(tmpPros);
    toast.error("Pro Removed");
  };

  const handleAddProduct = (product: IProduct, isNew?: boolean) => {
    const tmpProducts = isNew ? [...newProducts] : [...products];
    // TODO make ID existance compulsory
    // if (product.id && tmpProducts.find(e => e.id === product.id)) {
    if (product.title && !tmpProducts.find((e) => e.title === product.title)) {
      tmpProducts.push(product);
      if (isNew) setNewProducts(tmpProducts);
      else setProducts(tmpProducts);
    }
  };

  const handleRemoveProduct = (i: number) => {
    const tmpProducts = [...products];
    tmpProducts.splice(i, 1);
    setProducts(tmpProducts);
    toast.error("Product Removed");
  };

  // Handle General Form Changes
  const handleSaveChanges = async (values: any) => {
    // Plan fields are internal-only and written by the API's single plan
    // writer; helper fields (unlimited_products) never leave the form.
    const {
      unlimited_products: unlimitedProducts,
      max_products: maxProducts,
      max_pdp_score_interval: maxPdpScoreInterval,
      max_cv_analysis_interval: maxCvInterval,
      max_aip_analysis_interval: maxAipInterval,
      plan,
      ...rest
    } = values;
    const params: any = rest;
    if (isInternal(role) && !isHidden("plan")) {
      params.plan = plan;
      if (unlimitedProducts) params.max_products = null;
      else if (maxProducts !== "" && maxProducts !== null && maxProducts !== undefined) {
        params.max_products = Number(maxProducts);
      }
      if (maxPdpScoreInterval) params.max_pdp_score_interval = maxPdpScoreInterval;
      if (maxCvInterval) params.max_cv_analysis_interval = maxCvInterval;
      if (maxAipInterval) params.max_aip_analysis_interval = maxAipInterval;
    }
    setISubmitting(true);

    try {
      if (logo?.file && logo?.extension) {
        const { file, extension } = logo;
        params.logo_image = await uploadImage(file, Math.random(), extension);
      }

      const productIds = products
        .map((e) => e.desireid)
        .filter((e) => !!e) as Array<string>;
      const prosIds = pros
        .map((e) => e.userid)
        .filter((e) => !!e) as Array<number>;

      const contentTypeIds = contentTypes.map((ct) => ct.id);
      await saveChanges(
        params,
        productIds,
        newProducts,
        prosIds,
        contentTypeIds,
      );
      setISubmitting(false);
    } catch (error) {
      setISubmitting(false);
      console.error((error as Error)?.message);
      // 409 = name collides with another brand (API message names it)
      if (!handleConflictError(error)) toast.error("Brand edit error");
    }
  };

  React.useEffect(() => {
    if (prevProducts?.length) {
      setProducts(prevProducts);
    }
    if (prevPros?.length) setPros(prevPros);
    if (brandData?.logo)
      setLogo({ source: brandData.logo, file: brandData.logo });

    if (brandData?.content_types?.length) {
      setContentTypes(brandData.content_types);
    }
  }, [prevProducts, prevPros, brandData?.logo, brandData?.content_types]);

  return (
    <div className="basis-5/7 bg-surface rounded-lg box-shadow-1 py-4 px-4 sm:px-7 flex flex-col h-full">
      <Formik
        initialValues={{
          description: brandData?.description || "",
          name: brandData?.name || initialBrandName || "",
          embed: brandData?.embed || false,
          plan: (brandData?.plan as BrandPlan) || defaultPlan || "standard_brand",
          max_products:
            brandData?.max_products === null || brandData?.max_products === undefined
              ? ""
              : String(brandData.max_products),
          unlimited_products: brandData ? brandData.max_products === null : false,
          max_pdp_score_interval: brandData?.max_pdp_score_interval || "",
          max_cv_analysis_interval: brandData?.max_cv_analysis_interval || "",
          max_aip_analysis_interval: brandData?.max_aip_analysis_interval || "",
          pre_approval_downloading:
            brandData?.pre_approval_downloading || false,
          notifications_disabled: brandData?.notifications_disabled ?? false,
          slack_channel: brandData?.slack_channel || "",
          cv_subreddits: (brandData as any)?.cv_subreddits || "",
          cv_keywords: (brandData as any)?.cv_keywords || "",
          aip_competitors: (brandData as any)?.aip_competitors || "",
          aip_prompts: (brandData as any)?.aip_prompts || "",
          ...Object.fromEntries(
            BRIEF_BRAND_ASSET_KEYS.map((key) => [key, (brandData as any)?.[key] || ""])
          ),
        }}
        validationSchema={SignupSchema}
        enableReinitialize
        onSubmit={(values) => {
          if (isSales) return;
          handleSaveChanges(values);
        }}
      >
        {({ errors, touched, values, setFieldValue }) => {
          React.useEffect(() => {
            if (onNameChange) onNameChange(values?.name || "");
          }, [values?.name]);

          return (
            <Form
              className={classNames("w-full flex flex-col", {
                "h-[80vh]": isDialog,
              })}
            >
              <div
                className={classNames("overflow-y-auto flex-1 pr-2", {
                  " border-y border-border-token h-[70vh]": isDialog,
                })}
              >
                <FieldText
                  name="name"
                  label="Brand Name"
                  fullWidth
                  required
                  className={`appearance-none text-sm border w-full  p-4 rounded-xl text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-2 ${
                    errors.name && touched.name
                      ? "border-error"
                      : "border-border-token"
                  }`}
                />
                <div className="mt-3">
                  <FieldTextArea
                    required
                    label="Description"
                    name="description"
                    type="text"
                    rows={5}
                    errorClassNames="mt-0"
                    className={`${
                      errors.name && touched.name
                        ? "border-error"
                        : "border-border-token"
                    }`}
                  />
                </div>

                <div className="mt-3">
                  <FieldText
                    name="cv_subreddits"
                    label="Consumer Voice subreddits"
                    fullWidth
                    placeholder="e.g. headphones, audiophile, BudgetAudiophile"
                    className="appearance-none text-sm border border-border-token w-full p-4 rounded-xl text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-1"
                  />
                  <p className="text-xs text-text-2 mt-1">
                    Comma-separated subreddits the Consumer Voice analysis searches first for every product of this
                    brand (each product can add its own on its Consumer Voice tab). Leave empty to use category defaults.
                  </p>
                </div>

                <div className="mt-3">
                  <FieldText
                    name="cv_keywords"
                    label="Consumer Voice search terms"
                    fullWidth
                    placeholder="e.g. bose speaker, soundlink"
                    className="appearance-none text-sm border border-border-token w-full p-4 rounded-xl text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-1"
                  />
                  <p className="text-xs text-text-2 mt-1">
                    Comma-separated search terms Google Trends and Keyword Planner query for every product of this
                    brand, after the product&apos;s own terms. Leave empty to derive them from the product names.
                  </p>
                </div>

                <div className="mt-3">
                  <FieldText
                    name="aip_competitors"
                    label="AI Presence competitors"
                    fullWidth
                    placeholder="e.g. JBL, Sonos, Marshall"
                    className="appearance-none text-sm border border-border-token w-full p-4 rounded-xl text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-1"
                  />
                  <p className="text-xs text-text-2 mt-1">
                    Comma-separated competitors the AI Presence analysis tracks for every product of this brand (each
                    product can add its own on its AI Presence tab).
                  </p>
                </div>

                <div className="mt-3">
                  <FieldTextArea
                    required={false}
                    label="AI Presence prompts"
                    name="aip_prompts"
                    type="text"
                    rows={3}
                    errorClassNames="mt-0"
                    className="border-border-token"
                  />
                  <p className="text-xs text-text-2 mt-1">
                    Shopping questions to always benchmark for every product of this brand, one per line (up to 10).
                  </p>
                </div>

                {/* TDC-1722: every new brief on this brand starts from these.
                    Uploads need the brand's id, so files wait until it exists. */}
                <div className="mt-6">
                  <BriefBrandAssetsFields
                    heading="Brief Assets"
                    uploadUrlFor={(kind) =>
                      brandData?.id ? brandBriefAssetUpload(brandData.id, kind) : undefined
                    }
                    disabled={isSales}
                  />
                </div>

                {isInternal(role) && (
                  <div className="mt-3">
                    <span className="text-base font-bold text-left block mb-2 mt-3">
                      Slack Channel
                    </span>
                    <SlackChannelPicker
                      name="slack_channel"
                      brand={
                        brandData
                          ? { name: values.name, rmn: isNetworkPlan(values.plan) }
                          : null
                      }
                      hideCreate
                      hideLabel
                    />
                    <p className="text-xs text-text-2 mt-1">
                      New projects for this brand will inherit this channel as their default.
                    </p>
                  </div>
                )}

                <div className="mt-3">
                  <ProductImage
                    image={logo}
                    label="Brand Image"
                    setImage={setLogo}
                    height={250}
                    width={250}
                  />
                </div>

                {!isHidden("embed") && (
                  <div>
                    <span className="text-base font-bold text-left block mb-4 mt-3">
                      Video Player & QR Enabled
                    </span>
                    <FieldSwitch
                      name="embed"
                      label="Video Player & QR Enabled"
                      value={brandData?.embed}
                    />
                  </div>
                )}
                {!isHidden("plan") && isInternal(role) && (
                  <div className="mt-3">
                    <span className="text-base font-bold text-left block mb-2 mt-3">
                      Plan
                    </span>
                    <CustomFieldSelect name="plan" label="" required={false} placeholder="" fullWidth>
                      {BRAND_PLANS.map((p) => (
                        <MenuItem key={p} value={p}>
                          {PLAN_LABELS[p]}
                        </MenuItem>
                      ))}
                    </CustomFieldSelect>
                    <p className="text-xs text-text-2 mt-1 mb-3">
                      Network plans (Enterprise RMN, Enterprise Agency) unlock Catalog &gt; Brands and child brands.
                      Changing the plan resets the product limit and the analysis intervals to the plan defaults unless set below.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <FieldText
                          name="max_products"
                          label="Product limit"
                          type="number"
                          fullWidth
                          disabled={!!values.unlimited_products}
                          inputProps={{ min: 0, step: 1 }}
                        />
                        <p className="text-xs text-text-2 mt-1">
                          Caps how many products are PDP-scored. Importing is never limited; extra products land with
                          scoring paused.
                        </p>
                        <label className="flex items-center gap-2 mt-1 text-sm">
                          <input
                            type="checkbox"
                            checked={!!values.unlimited_products}
                            onChange={(e) => setFieldValue("unlimited_products", e.target.checked)}
                          />
                          Unlimited
                        </label>
                        {productCount !== undefined && (
                          <p
                            className={classNames("text-xs mt-1", {
                              "text-down":
                                !values.unlimited_products &&
                                values.max_products !== "" &&
                                productCount > Number(values.max_products),
                              "text-text-2": !(
                                !values.unlimited_products &&
                                values.max_products !== "" &&
                                productCount > Number(values.max_products)
                              ),
                            })}
                          >
                            Products with scoring active: {productCount} /{" "}
                            {capLabel(values, brandData?.max_products)}
                          </p>
                        )}
                      </div>
                      <div>
                        <CustomFieldSelect
                          name="max_pdp_score_interval"
                          label="PDP analysis interval"
                          required={false}
                          placeholder=""
                          fullWidth
                        >
                          <MenuItem value="">Plan default</MenuItem>
                          {PDP_SCORE_INTERVALS.map((i) => (
                            <MenuItem key={i} value={i}>
                              {INTERVAL_LABELS[i]}
                            </MenuItem>
                          ))}
                        </CustomFieldSelect>
                        <p className="text-xs text-text-2 mt-1">
                          The fastest interval any of this brand&apos;s product pages may be analyzed at. Each page
                          sets its own (default weekly, or this cap if slower) and the day it runs on.
                        </p>
                      </div>
                      <div>
                        <CustomFieldSelect
                          name="max_cv_analysis_interval"
                          label="Consumer Voice analysis interval"
                          required={false}
                          placeholder=""
                          fullWidth
                        >
                          <MenuItem value="">Plan default</MenuItem>
                          {PDP_SCORE_INTERVALS.map((i) => (
                            <MenuItem key={i} value={i}>
                              {INTERVAL_LABELS[i]}
                            </MenuItem>
                          ))}
                        </CustomFieldSelect>
                        <p className="text-xs text-text-2 mt-1">
                          The fastest interval this brand&apos;s products may run Consumer Voice at. Each product
                          sets its own schedule in Run CV Analysis; it runs while a page of the product is on
                          Autopilot.
                        </p>
                      </div>
                      <div>
                        <CustomFieldSelect
                          name="max_aip_analysis_interval"
                          label="AI Presence analysis interval"
                          required={false}
                          placeholder=""
                          fullWidth
                        >
                          <MenuItem value="">Plan default</MenuItem>
                          {PDP_SCORE_INTERVALS.map((i) => (
                            <MenuItem key={i} value={i}>
                              {INTERVAL_LABELS[i]}
                            </MenuItem>
                          ))}
                        </CustomFieldSelect>
                        <p className="text-xs text-text-2 mt-1">
                          The fastest interval this brand&apos;s products may run AI Presence at. Each product sets
                          its own schedule in Run AI Presence; it runs while a page of the product is on Autopilot.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {!isHidden("preApproval") && (
                  <div>
                    <span className="text-base font-bold text-left block mb-4 mt-3">
                      Pre-Approval Downloading
                    </span>
                    <FieldSwitch
                      name="pre_approval_downloading"
                      label="Pre-Approval Downloading"
                      value={brandData?.pre_approval_downloading}
                    />
                  </div>
                )}

                {!isHidden("notifications") && isInternal(role) && (
                  <div>
                    <span className="text-base font-bold text-left block mb-4 mt-3">
                      Disable Notifications
                    </span>
                    <FieldSwitch
                      name="notifications_disabled"
                      label="Disable all notifications for this brand (in-platform and email)"
                      value={brandData?.notifications_disabled}
                    />
                  </div>
                )}

                {/* Network Brands section moved to the Network tab inside Brand Details (rmn/NetworkBrandsPanel) */}

                {/* Pros section commented out - Pros are now added via the Experts tab inside Brand Details after a Brand is added */}
                {/* {isDialog && (
                  <div>
                    <span className="text-base font-bold text-left block mb-4 mt-3">
                      Pros
                    </span>

                    <div className="relative">
                      <BrandProList onAddition={handleAddPros} />
                    </div>

                    {pros.map((pro, i) => (
                      <div className="relative mb-4" key={i}>
                        <div className="w-full text-left placeholder-opacity-50 text-base focus:outline-primary active:outline-primary border  focus:outline-none focus:shadow-outline border-dark border-opacity-10  mb-4 p-3 rounded-lg">
                          {pro.displayName}
                        </div>
                        <div
                          className="text-primary text-base absolute bg-white right-0 mr-3 mt-3 top-0"
                          onClick={() => handleRemovePros(i)}
                        >
                          <IconClose />
                        </div>
                      </div>
                    ))}
                  </div>
                )} */}

                {/* Products section commented out - Products are now added via the Products tab inside Brand Details after a Brand is added */}
                {/* {isDialog && (
                  <div>
                    <span className="text-base font-bold text-left block mb-4 mt-3">
                      Products
                    </span>

                    <div className="relative">
                      <BrandProductList onAddition={handleAddProduct} />
                    </div>

                    {[...products, ...newProducts].map((product, i) => {
                      const isNewProduct = i >= products.length;
                      const actualIndex = isNewProduct
                        ? i - products.length
                        : i;

                      return (
                        <div className="relative mb-4" key={i}>
                          <div className="w-full text-left placeholder-opacity-50 text-base focus:outline-primary active:outline-primary border  focus:outline-none focus:shadow-outline border-dark border-opacity-10  mb-4 p-3 rounded-lg">
                            {product.title}
                          </div>
                          <div
                            className="text-primary text-base absolute bg-white right-0 mr-3 mt-3 top-0"
                            onClick={() => {
                              if (isNewProduct) {
                                const tmpNewProducts = [...newProducts];
                                tmpNewProducts.splice(actualIndex, 1);
                                setNewProducts(tmpNewProducts);
                              } else {
                                handleRemoveProduct(actualIndex);
                              }
                            }}
                          >
                            <IconClose />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )} */}

                {!isHidden("contentTypes") && (brandIsNetwork(brandData) || isNetworkPlan(values.plan)) && (
                  <ContentTypeSelector
                    contentTypes={contentTypes}
                    onUpdate={setContentTypes}
                  />
                )}
              </div>
              <div className="flex items-center justify-end pt-4">
                <button
                  className={classNames(
                    "text-base py-3 px-5 lg:px-10 block rounded-xl focus:outline-none focus:shadow-outline",
                    {
                      "bg-accent text-white cursor-pointer":
                        !isSubmitting && !isSales,
                      "bg-surface-2 text-text-2 cursor-not-allowed":
                        isSubmitting || isSales,
                    },
                  )}
                  type="submit"
                  disabled={isSubmitting || isSales}
                >
                  {brandData ? (
                    <React.Fragment key={brandData?.id}>
                      {isSubmitting ? "Saving..." : "Save changes"}
                    </React.Fragment>
                  ) : (
                    <React.Fragment key="create-brand-form">
                      {isSubmitting ? "Creating..." : "Create brand"}
                    </React.Fragment>
                  )}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default BrandForm;
